import { useState, useEffect, useRef, useCallback, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { IfGuides } from "@/pages/guides/GuidesOnly";
import { guidePath } from "@/content/guides";
import { Input } from "@/components/ui/input";
import {
  Search,
  Calendar as CalendarIcon,
  ExternalLink,
  Loader2,
  Image as ImageIcon,
  Clock,
  ChevronsUpDown,
  ScanFace,
  LayoutGrid,
  Upload,
  Camera,
  Hash,
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  Sparkles,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { COUNTRIES } from "@/lib/countries";
import { formatDate } from "@/lib/datetime";
import { cn } from "@/lib/utils";
import galleryPlaceholder from "@/assets/gallery-placeholder.jpg";

import {
  COUNTRIES_KEY,
  DEFAULT_COUNTRY,
  DIRECTORY_PAGE_SIZE,
  GALLERY_LIST_ENDPOINT,
  INITIAL_COUNTRY_KEY,
  countryFromLanguages,
  countryLabel,
  directoryHref,
  directoryKey,
  fetchGalleryList,
  galleryDate,
  galleryLocation,
  hitCursor,
  readDirectoryParams,
  type CountryBucket,
  type GalleryHit,
  type GalleryListPage,
} from "@/lib/gallery/directory";
import { baseLang } from "@/lib/seo/site";
import { SsrResponseContext } from "@/lib/ssr";

type SearchResponse = GalleryListPage;

const ENDPOINT = GALLERY_LIST_ENDPOINT;

const WIZARD_COUNTRIES = ["MY", "PH", "MX"];
const supportsWizard = (code: string) => WIZARD_COUNTRIES.includes(code.toUpperCase());

/** The visitor's country from the browser's languages (the server does the same from Accept-Language). */
const detectCountry = (): string => {
  try {
    const langs = [...(navigator.languages || []), navigator.language].filter(Boolean);
    return countryFromLanguages(langs) ?? DEFAULT_COUNTRY;
  } catch {
    return DEFAULT_COUNTRY;
  }
};

const flagEmoji = (code: string): string => {
  if (!code || code.length !== 2) return "🏳️";
  const A = 0x1f1e6;
  const up = code.toUpperCase();
  return String.fromCodePoint(A + up.charCodeAt(0) - 65, A + up.charCodeAt(1) - 65);
};


const MATCH_ENDPOINT_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gallery-match-proxy`;

const fileToBase64Util = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const parseGalleryUrlUtil = (url: string): { hostname: string; slug: string } | null => {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    return { hostname: u.hostname, slug: parts[parts.length - 1] || "" };
  } catch {
    return null;
  }
};

const appendGuidsToUrl = (url: string, guids?: string[]): string => {
  if (!guids || guids.length === 0) return url;
  try {
    const u = new URL(url);
    u.searchParams.delete("guids");
    guids.forEach((g) => u.searchParams.append("guids", g));
    return u.toString();
  } catch {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}${guids.map((g) => `guids=${encodeURIComponent(g)}`).join("&")}`;
  }
};

const GallerySearchDialog = ({
  hit,
  open,
  onOpenChange,
}: {
  hit: GalleryHit;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) => {
  const [selfie, setSelfie] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [bib, setBib] = useState("");
  const [searching, setSearching] = useState(false);
  const [thumbs, setThumbs] = useState<string[] | null>(null);
  const [guids, setGuids] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const onPick = (f: File | null) => {
    setSelfie(f);
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setSelfiePreview(f ? URL.createObjectURL(f) : null);
  };

  const reset = () => {
    onPick(null);
    setBib("");
    setThumbs(null);
    setGuids([]);
    setError(null);
  };

  useEffect(() => {
    if (!open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSearch = !!(selfie || bib.trim());

  const run = async () => {
    if (!canSearch) return;
    const parsed = parseGalleryUrlUtil(hit.url);
    if (!parsed) {
      setError("Could not parse gallery URL");
      return;
    }
    setSearching(true);
    setError(null);
    setThumbs(null);
    try {
      const selfieB64 = selfie ? await fileToBase64Util(selfie) : null;
      const device = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? "mobile" : "desktop";
      const res = await fetch(MATCH_ENDPOINT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          hostname: parsed.hostname,
          slug: parsed.slug,
          device,
          format: "jpg",
          text: null,
          timestamp_from: null,
          timestamp_to: null,
          groups: [{ selfie: selfieB64, text: bib.trim() }],
        }),
      });
      const data = await res.json();
      const photoHits = Array.isArray(data?.photos?.hits) ? data.photos.hits : [];
      const photos = photoHits
        .map((p: any) => ({
          md: p?.previews?.md?.location as string | undefined,
          lg: p?.previews?.lg?.location as string | undefined,
          guid: (p?.guid ?? p?.id ?? p?._id) as string | undefined,
        }))
        .filter((p: any) => !!p.md || !!p.lg);
      setThumbs(photos.map((p: any) => p.md || p.lg).filter(Boolean));
      setGuids(photos.map((p: any) => p.guid).filter((g: any) => !!g));
    } catch (e) {
      console.error(e);
      setError("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const galleryUrl = appendGuidsToUrl(hit.url, guids);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display line-clamp-2">{hit.name}</DialogTitle>
        </DialogHeader>

        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        />

        {thumbs === null ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Add a selfie and/or bib number to find your photos in this gallery.
            </p>

            {selfiePreview ? (
              <div className="flex items-center gap-3">
                <img src={selfiePreview} alt="Selfie" className="w-20 h-20 object-cover rounded-xl ring-2 ring-hawk-orange/30" />
                <div className="flex gap-2 flex-1">
                  <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => cameraRef.current?.click()}>
                    <Camera size={14} /> Retake
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => onPick(null)}>
                    <X size={14} /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-1 p-4 rounded-xl border-2 border-dashed border-border hover:border-hawk-orange hover:bg-hawk-orange/5 transition-colors"
                >
                  <Camera size={22} className="text-hawk-orange" />
                  <span className="text-xs font-semibold">Take selfie</span>
                </button>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-1 p-4 rounded-xl border-2 border-dashed border-border hover:border-hawk-orange hover:bg-hawk-orange/5 transition-colors"
                >
                  <Upload size={22} className="text-hawk-orange" />
                  <span className="text-xs font-semibold">Upload</span>
                </button>
              </div>
            )}

            <Input
              inputMode="numeric"
              placeholder="Bib number (e.g. 1423)"
              value={bib}
              onChange={(e) => setBib(e.target.value.replace(/\s+/g, "").slice(0, 12))}
              className="h-12 text-center font-display font-semibold tracking-widest"
            />

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button
              disabled={!canSearch || searching}
              onClick={run}
              className="w-full h-12 bg-hawk-orange hover:bg-hawk-orange/90"
            >
              {searching ? <><Loader2 size={16} className="animate-spin" /> Searching…</> : <><Sparkles size={16} /> Find my photos</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {thumbs.length > 0 ? (
              <>
                <p className="text-sm">
                  <span className="font-display font-semibold">{thumbs.length}</span>{" "}
                  <span className="text-muted-foreground">{thumbs.length === 1 ? "match" : "matches"} found</span>
                </p>
                <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                  {thumbs.slice(0, 30).map((src, i) => (
                    <img key={i} src={src} alt={`Match ${i + 1}`} loading="lazy" className="w-full aspect-square object-cover rounded-lg bg-muted" />
                  ))}
                </div>
                <Button asChild className="w-full h-12 bg-hawk-orange hover:bg-hawk-orange/90">
                  <a href={galleryUrl} target="_blank" rel="noopener noreferrer">
                    View in gallery <ExternalLink size={16} />
                  </a>
                </Button>
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-muted-foreground mb-4">No matches found in this gallery.</p>
                <Button variant="outline" onClick={reset}>Try again</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const GalleryCard = ({
  hit,
  isUpcoming,
  eager,
}: {
  hit: GalleryHit;
  isUpcoming?: boolean;
  /** The first row: load the cover straight away (it's what the page paints first). */
  eager?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const lang = baseLang(i18n.language);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = galleryLocation(hit, lang);
  return (
    <Card
      className={`overflow-hidden h-full flex flex-col hover:shadow-xl transition-shadow duration-300 border-border ${
        isUpcoming ? "opacity-80" : ""
      }`}
    >
      <a href={hit.url} target="_blank" rel="noopener noreferrer" className="group block">
        <div className="relative aspect-[4/3] bg-muted overflow-hidden">
          <img
            src={hit.media?.featuredImage || galleryPlaceholder}
            alt={hit.name}
            loading={eager ? "eager" : "lazy"}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            style={hit.mediaBackgroundPosition ? { objectPosition: hit.mediaBackgroundPosition } : undefined}
          />
          {isUpcoming && (
            <Badge className="absolute top-3 right-3 bg-hawk-orange/90 text-white border-0">
              <Clock size={12} className="mr-1" />
              {t("findPhotos.upcoming")}
            </Badge>
          )}
        </div>
        <div className="p-4 pb-2">
          <h3 className="font-display font-semibold text-foreground group-hover:text-hawk-orange transition-colors line-clamp-2 mb-1">
            {hit.name}
          </h3>
          {location && <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{location}</p>}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CalendarIcon size={14} />
              <span>{galleryDate(hit, lang)}</span>
            </div>
            <div className="flex items-center gap-3">
              {!isUpcoming && hit.noItems != null && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ImageIcon size={14} />
                  <span>{hit.noItems.toLocaleString("en-GB")}</span>
                </div>
              )}
              <ExternalLink size={14} className="text-muted-foreground" />
            </div>
          </div>
        </div>
      </a>
      {!isUpcoming && (
        <div className="px-4 pb-4 mt-auto pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full border-hawk-orange/30 text-hawk-orange hover:bg-hawk-orange/10 hover:text-hawk-orange"
            onClick={() => setSearchOpen(true)}
          >
            <ScanFace size={14} /> {t("findPhotos.findMeInGallery")}
          </Button>
          <GallerySearchDialog hit={hit} open={searchOpen} onOpenChange={setSearchOpen} />
        </div>
      )}
    </Card>
  );
};

/* ---------- Country picker (shared) ---------- */
const CountryPicker = ({
  country,
  setCountry,
}: {
  country: string;
  setCountry: (c: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { t, i18n } = useTranslation();
  const countryName = countryLabel(country, baseLang(i18n.language));

  const popular = useMemo(() => {
    const codes = ["US", "GB", "MY", "PH", "IE", "MX", "CO"];
    return codes.map((c) => COUNTRIES.find((x) => x.code === c)).filter((x): x is { code: string; name: string } => !!x);
  }, []);

  const filtered = useMemo(() => {
    const v = q.trim().toLowerCase();
    if (!v) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(v) || c.code.toLowerCase().includes(v));
  }, [q]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-14 rounded-xl bg-card border-border shadow-lg justify-between sm:w-44 px-4 gap-2">
          <span className="text-2xl leading-none">{flagEmoji(country)}</span>
          <span className="truncate flex-1 text-left">{countryName}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("findPhotos.selectCountry")}</DialogTitle>
        </DialogHeader>
        <Input placeholder={t("findPhotos.searchCountry")} value={q} onChange={(e) => setQ(e.target.value)} className="mb-2" />
        <div className="overflow-y-auto -mx-2 px-2 space-y-4">
          {!q.trim() && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-1">{t("findPhotos.popularCountries")}</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {popular.map((c) => {
                  const selected = country === c.code;
                  return (
                    <button
                      key={`pop-${c.code}`}
                      type="button"
                      onClick={() => {
                        setCountry(c.code);
                        setOpen(false);
                        setQ("");
                      }}
                      className={cn(
                        "flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors text-center",
                        selected ? "border-hawk-orange bg-hawk-orange/10" : "border-border hover:bg-muted",
                      )}
                    >
                      <span className="text-3xl leading-none">{flagEmoji(c.code)}</span>
                      <span className="text-xs text-foreground line-clamp-2">{c.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            {!q.trim() && (
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-1">{t("findPhotos.allCountries")}</p>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {filtered.map((c) => {
                const selected = country === c.code;
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => {
                      setCountry(c.code);
                      setOpen(false);
                      setQ("");
                    }}
                    className={cn(
                      "flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors text-center",
                      selected ? "border-hawk-orange bg-hawk-orange/10" : "border-border hover:bg-muted",
                    )}
                  >
                    <span className="text-3xl leading-none">{flagEmoji(c.code)}</span>
                    <span className="text-xs text-foreground line-clamp-2">{c.name}</span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <p className="col-span-full text-center text-sm text-muted-foreground py-6">{t("findPhotos.noCountry")}</p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Browse view ---------- */
// The directory: a country's galleries, newest first (or before ?before=), 50 at a time. The first page comes from
// the SSR prefetch (src/lib/gallery/ssrPrefetch.ts) through the React Query cache, so it's in the server HTML as
// real links; scrolling loads more in place, and "Older galleries" is a real link to the next page for crawlers
// (and anyone without JavaScript), with links to every country's listing below.
const BrowseView = ({
  country,
  setCountry,
  before,
}: {
  country: string;
  setCountry: (c: string) => void;
  /** The ?before= cursor this page starts from (null: the newest). */
  before: string | null;
}) => {
  const { t, i18n } = useTranslation();
  const lang = baseLang(i18n.language);
  const queryClient = useQueryClient();
  const initial = queryClient.getQueryData<GalleryListPage>(directoryKey(country, before));
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [hits, setHits] = useState<GalleryHit[]>(initial?.hits ?? []);
  const [total, setTotal] = useState(initial?.total ?? 0);
  const [loading, setLoading] = useState(!initial);
  const [loadingMore, setLoadingMore] = useState(false);
  // More after this page? A full page, and (on the newest page, where the total counts them all) not the last one.
  const moreAfter = (page: GalleryListPage, fromStart: boolean) =>
    page.hits.length >= DIRECTORY_PAGE_SIZE && (!fromStart || page.total > page.hits.length);
  const [hasMore, setHasMore] = useState(initial ? moreAfter(initial, !before) : true);
  // A page past the end (an old ?before= link) has nothing on it: a real 404 from the server, not a thin 200.
  const ssrResponse = useContext(SsrResponseContext);
  if (ssrResponse && before && initial && initial.hits.length === 0) ssrResponse.status = 404;
  // The server already rendered the first page: don't fetch it again on mount.
  const skipFirstFetch = useRef(!!initial);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const countryName = countryLabel(country, lang);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 400);
    return () => clearTimeout(timer);
  }, [query]);

  const fetchGalleries = useCallback(
    async (searchAfter?: string, append = false) => {
      if (append) setLoadingMore(true);
      else setLoading(true);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const data: SearchResponse = await fetchGalleryList(
          {
            text: debouncedQuery || null,
            country,
            // A text search covers every date; the plain listing starts at ?before=.
            searchAfter: searchAfter ?? (debouncedQuery ? null : before),
          },
          { signal: controller.signal },
        );

        if (append) setHits((prev) => [...prev, ...data.hits]);
        else setHits(data.hits);
        setTotal(data.total);
        setHasMore(append ? data.hits.length >= DIRECTORY_PAGE_SIZE : moreAfter(data, !before && !debouncedQuery));
      } catch (err: unknown) {
        if ((err as { name?: string })?.name !== "AbortError") console.error("Failed to fetch galleries:", err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedQuery, country, before],
  );

  useEffect(() => {
    if (skipFirstFetch.current) {
      skipFirstFetch.current = false;
      return;
    }
    setHits([]);
    setHasMore(true);
    fetchGalleries();
  }, [fetchGalleries]);

  const nextCursor = hitCursor(hits[hits.length - 1]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore && nextCursor) {
          fetchGalleries(nextCursor, true);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, nextCursor, fetchGalleries]);

  const now = Date.now();
  const isFuture = (h: GalleryHit) => new Date(typeof h.date === "number" ? h.date : String(h.date)).getTime() > now;
  const pastHits = hits.filter((h) => !isFuture(h));
  const upcomingHits = hits.filter(isFuture);

  const renderGrouped = (items: GalleryHit[], isUpcoming = false) => (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
      {items.map((hit, index) => (
        <GalleryCard key={`${hit.url}-${index}`} hit={hit} isUpcoming={isUpcoming} eager={index < 4} />
      ))}
    </div>
  );

  return (
    <>
      <div className="max-w-lg mx-auto flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
          <Input
            type="text"
            placeholder={t("findPhotos.searchPlaceholder")}
            aria-label={t("findPhotos.searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-12 h-14 text-lg rounded-xl bg-card border-border shadow-lg"
          />
        </div>
        <CountryPicker country={country} setCountry={setCountry} />
      </div>

      <section className="py-12" aria-labelledby="directory-heading">
        <div className="container mx-auto px-4">
          <h2 id="directory-heading" className="text-2xl font-display font-bold text-foreground mb-2 max-w-6xl mx-auto">
            {t("findPhotos.galleriesIn", { country: countryName })}
          </h2>
          {!loading && (
            <p className="text-sm text-muted-foreground mb-6 max-w-6xl mx-auto">
              {total.toLocaleString("en-GB")} {t("findPhotos.galleries")} · {countryName}
            </p>
          )}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-hawk-orange" size={32} />
            </div>
          )}
          {!loading && (
            <div className="space-y-16 max-w-6xl mx-auto">
              {upcomingHits.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <Clock size={20} className="text-hawk-orange" />
                    <h3 className="text-xl font-display font-bold text-foreground">{t("findPhotos.upcomingGalleries")}</h3>
                    <span className="text-sm text-muted-foreground">({upcomingHits.length})</span>
                  </div>
                  {renderGrouped(upcomingHits, true)}
                </div>
              )}
              {pastHits.length > 0 && (
                <div>
                  {upcomingHits.length > 0 && (
                    <div className="flex items-center gap-2 mb-6">
                      <ImageIcon size={20} className="text-hawk-orange" />
                      <h3 className="text-xl font-display font-bold text-foreground">{t("findPhotos.availableGalleries")}</h3>
                      <span className="text-sm text-muted-foreground">({pastHits.length})</span>
                    </div>
                  )}
                  {renderGrouped(pastHits)}
                </div>
              )}
            </div>
          )}
          {!loading && hits.length === 0 && (
            <div className="text-center py-20">
              <img src={galleryPlaceholder} alt={t("findPhotos.noResults")} className="mx-auto w-16 h-16 opacity-40 mb-4" />
              <p className="text-muted-foreground">{t("findPhotos.noResultsDesc")}</p>
            </div>
          )}
          <div ref={sentinelRef} className="h-4" />
          {loadingMore && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-hawk-orange" size={24} />
            </div>
          )}
          {/* Crawlable paging: the next page is a real URL. With JavaScript, clicking loads it in place. */}
          {!loading && !debouncedQuery && (hasMore || before) && (
            <nav aria-label={t("findPhotos.pagination")} className="flex flex-wrap justify-center gap-3 pt-4">
              {before && (
                <Button variant="outline" asChild>
                  <Link to={directoryHref(country, null, lang)}>
                    <ArrowLeft size={16} /> {t("findPhotos.newestGalleries")}
                  </Link>
                </Button>
              )}
              {hasMore && nextCursor && (
                <Button variant="outline" asChild>
                  <a
                    href={directoryHref(country, nextCursor, lang)}
                    rel="next"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!loadingMore) fetchGalleries(nextCursor, true);
                    }}
                  >
                    {t("findPhotos.olderGalleries")} <ArrowRight size={16} />
                  </a>
                </Button>
              )}
            </nav>
          )}
          {hasMore && nextCursor && !debouncedQuery && (
            <Helmet>
              <link rel="next" href={directoryHref(country, nextCursor, lang)} />
            </Helmet>
          )}

        </div>
      </section>
    </>
  );
};

/* ---------- Every country's listing (links) ---------- */
const CountryDirectory = () => {
  const { t, i18n } = useTranslation();
  const lang = baseLang(i18n.language);
  // Server-prefetched (and hydrated) on a server render; fetched in the browser after a client-side navigation.
  const { data } = useQuery({
    queryKey: COUNTRIES_KEY,
    queryFn: async (): Promise<CountryBucket[]> => (await fetchGalleryList({})).countries ?? [],
    staleTime: 5 * 60_000,
  });
  const countries = (data ?? []).filter((b) => /^[a-z]{2}$/i.test(b.key) && b.doc_count > 0);
  if (!countries.length) return null;
  return (
    <section className="pb-16" aria-labelledby="countries-heading">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 id="countries-heading" className="text-xl font-display font-bold text-foreground mb-4">
          {t("findPhotos.byCountry")}
        </h2>
        <ul className="flex flex-wrap gap-2">
          {countries.map((b) => (
            <li key={b.key}>
              <Link
                to={directoryHref(b.key, null, lang)}
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm hover:border-hawk-orange hover:text-hawk-orange transition-colors"
              >
                <span aria-hidden="true">{flagEmoji(b.key)}</span>
                {countryLabel(b.key, lang)}
                <span className="text-muted-foreground">({b.doc_count.toLocaleString("en-GB")})</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* ---------- Date presets ---------- */
type DateRange = { from: Date; to: Date; label: string };

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const buildPresets = (): DateRange[] => {
  const today = new Date();
  const t0 = startOfDay(today);
  const t1 = endOfDay(today);
  const yesterday = new Date(t0);
  yesterday.setDate(yesterday.getDate() - 1);
  const twoDaysAgo = new Date(t0);
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

  return [
    { from: t0, to: t1, label: "Today" },
    { from: startOfDay(yesterday), to: endOfDay(yesterday), label: "Yesterday" },
    { from: startOfDay(twoDaysAgo), to: endOfDay(twoDaysAgo), label: "2 days ago" },
  ];
};

/* ---------- Wizard view ---------- */
type MatchHit = GalleryHit & { matchCount?: number; matchError?: boolean; thumbs?: string[]; fulls?: string[]; guids?: string[] };

const appendGuids = (url: string, guids?: string[]): string => {
  if (!guids || guids.length === 0) return url;
  try {
    const u = new URL(url);
    u.searchParams.delete("guids");
    guids.forEach((g) => u.searchParams.append("guids", g));
    return u.toString();
  } catch {
    const sep = url.includes("?") ? "&" : "?";
    const qs = guids.map((g) => `guids=${encodeURIComponent(g)}`).join("&");
    return `${url}${sep}${qs}`;
  }
};

const MATCH_ENDPOINT = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gallery-match-proxy`;
// Old and new plan names (free/pro/max from 1 November 2026).
const PLAN_RANK: Record<string, number> = { payg: 0, free: 0, lite: 1, pro: 2, enterprise: 3, max: 3 };

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // strip "data:*/*;base64," prefix
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const parseGalleryUrl = (url: string): { hostname: string; slug: string } | null => {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    const slug = parts[parts.length - 1] || "";
    return { hostname: u.hostname, slug };
  } catch {
    return null;
  }
};

// Normalize a gallery name into a stable "event" key.
// Strips years, common race/category/distance words, and keeps the first
// few significant tokens so variants like "ABC Marathon 2024 - 5K" and
// "ABC Marathon 2024 - Half" collapse to the same event.
const STOPWORDS = new Set([
  "5k", "10k", "15k", "21k", "42k", "1k", "2k", "3k",
  "half", "full", "marathon", "ultra", "sprint", "olympic", "relay",
  "fun", "run", "race", "ride", "walk", "swim", "tri", "triathlon",
  "men", "women", "male", "female", "open", "masters", "kids", "junior", "elite",
  "category", "cat", "wave", "day", "edition", "the", "and", "of", "for", "at", "in",
  "morning", "evening", "afternoon", "am", "pm",
  "photos", "gallery", "galleries", "event", "official",
]);

const eventKey = (name: string): string => {
  const tokens = name
    .toLowerCase()
    .replace(/\d{4}/g, " ")                  // years
    .replace(/\b\d+\s*(k|km|mi|m)\b/g, " ")  // distances like 10k, 21km
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((t) => t && t.length > 2 && !STOPWORDS.has(t));
  // Use first 3 significant tokens as the group key (fallback to whole name).
  const key = tokens.slice(0, 3).join(" ");
  return key || name.toLowerCase().trim();
};

const groupGalleries = (hits: GalleryHit[]): { key: string; label: string; hits: GalleryHit[] }[] => {
  const groups = new Map<string, GalleryHit[]>();
  for (const h of hits) {
    const k = eventKey(h.name);
    const arr = groups.get(k) ?? [];
    arr.push(h);
    groups.set(k, arr);
  }
  // Sort each group by date desc, and groups by their newest date desc.
  const arr = Array.from(groups.entries()).map(([key, hits]) => {
    hits.sort((a, b) => Number(b.date) - Number(a.date));
    // Pick the shortest name as the human label — usually the canonical event name.
    const label = [...hits].sort((a, b) => a.name.length - b.name.length)[0].name;
    return { key, label, hits };
  });
  arr.sort((a, b) => Number(b.hits[0].date) - Number(a.hits[0].date));
  return arr;
};

const WizardView = ({ country, setCountry }: { country: string; setCountry: (c: string) => void }) => {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [range, setRange] = useState<DateRange | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  
  const [selfie, setSelfie] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [bib, setBib] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<MatchHit[] | null>(null);
  const [searchProgress, setSearchProgress] = useState({ done: 0, total: 0 });
  const [availableGalleries, setAvailableGalleries] = useState<GalleryHit[] | null>(null);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number; galleryUrl: string; galleryName: string } | null>(null);

  const presets = useMemo(() => buildPresets(), []);

  const onSelfieChange = (f: File | null) => {
    setSelfie(f);
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setSelfiePreview(f ? URL.createObjectURL(f) : null);
  };

  const fetchAvailableGalleries = useCallback(async (r: DateRange, c: string) => {
    setLoadingAvailable(true);
    try {
      const params = new URLSearchParams();
      params.set("country", c.toLowerCase());
      params.set("from", r.from.toISOString());
      params.set("to", r.to.toISOString());
      const fromMs = Date.UTC(r.from.getUTCFullYear(), r.from.getUTCMonth(), r.from.getUTCDate());
      const toMs = Date.UTC(r.to.getUTCFullYear(), r.to.getUTCMonth(), r.to.getUTCDate());
      const allHits: GalleryHit[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < 10; page++) {
        const p = new URLSearchParams(params);
        if (cursor) p.set("searchAfter", cursor);
        const res = await fetch(`${ENDPOINT}?${p.toString()}`);
        const d: SearchResponse = await res.json();
        if (!d.hits?.length) break;
        allHits.push(...d.hits);
        cursor = hitCursor(d.hits[d.hits.length - 1]) ?? undefined;
        if (d.hits.length < 20) break;
      }
      const inRange = allHits.filter((h) => {
        const ms = Number(h.date);
        if (!Number.isFinite(ms)) return false;
        const dt = new Date(ms);
        const dayMs = Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
        return dayMs >= fromMs && dayMs <= toMs;
      });
      setAvailableGalleries(inRange);
    } catch (e) {
      console.error(e);
      setAvailableGalleries([]);
    } finally {
      setLoadingAvailable(false);
    }
  }, []);

  useEffect(() => {
    if (step === 2 && range) {
      fetchAvailableGalleries(range, country);
    }
  }, [step, range, country, fetchAvailableGalleries]);

  // Reset selection whenever the available list changes — user picks explicitly.
  useEffect(() => {
    setSelectedUrls(new Set());
  }, [availableGalleries]);

  const groupedAvailable = useMemo(
    () => (availableGalleries ? groupGalleries(availableGalleries) : []),
    [availableGalleries],
  );

  const canProceed2 = !!range;
  const canProceed3 = selectedUrls.size > 0;
  const canSearch = !!(selfie || bib.trim()) && selectedUrls.size > 0;

  const toggleUrl = (url: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };
  const toggleGroup = (hits: GalleryHit[]) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      const allSelected = hits.every((h) => next.has(h.url));
      if (allSelected) hits.forEach((h) => next.delete(h.url));
      else hits.forEach((h) => next.add(h.url));
      return next;
    });
  };

  const runSearch = async () => {
    if (!canSearch || !range) return;
    setSearching(true);
    setStep(4);
    setResults(null);
    setSearchProgress({ done: 0, total: 0 });
    try {
      // 1. The list of galleries to search comes from the user's selection
      // made in step 2 (filtered out of `availableGalleries`).
      const inRange: GalleryHit[] = (availableGalleries ?? []).filter((h) =>
        selectedUrls.has(h.url),
      );

      // 2. Build payload pieces
      const selfieB64 = selfie ? await fileToBase64(selfie) : null;
      const device = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? "mobile" : "desktop";
      const text = bib.trim();

      setSearchProgress({ done: 0, total: inRange.length });

      // 3. Parallel search per gallery
      const enrichedAll: MatchHit[] = await Promise.all(
        inRange.map(async (h): Promise<MatchHit> => {
          const parsed = parseGalleryUrl(h.url);
          if (!parsed) return { ...h, matchError: true };
          try {
            const res = await fetch(MATCH_ENDPOINT, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                hostname: parsed.hostname,
                slug: parsed.slug,
                device,
                format: "jpg",
                text: null,
                timestamp_from: null,
                timestamp_to: null,
                groups: [{ selfie: selfieB64, text }],
              }),
            });
            const data = await res.json();
            const photoHits = Array.isArray(data?.photos?.hits) ? data.photos.hits : [];
            const count = photoHits.length;
            const photos = photoHits
              .map((p: any) => ({
                md: p?.previews?.md?.location as string | undefined,
                lg: p?.previews?.lg?.location as string | undefined,
                guid: (p?.guid ?? p?.id ?? p?._id) as string | undefined,
              }))
              .filter((p: { md?: string; lg?: string }) => !!p.md || !!p.lg);
            const thumbs = photos.map((p) => p.md || p.lg!).filter(Boolean);
            const fulls = photos.map((p) => p.lg || p.md!).filter(Boolean);
            const guids = photos.map((p) => p.guid).filter((g): g is string => !!g);
            return { ...h, matchCount: count, thumbs, fulls, guids };
          } catch {
            return { ...h, matchError: true };
          } finally {
            setSearchProgress((p) => ({ ...p, done: p.done + 1 }));
          }
        }),
      );

      // 4. Filter to galleries with matches and sort by plan (highest first)
      const matched = enrichedAll
        .filter((h) => (h.matchCount ?? 0) > 0)
        .sort((a, b) => {
          const ar = PLAN_RANK[(a.plan || "").toLowerCase()] ?? -1;
          const br = PLAN_RANK[(b.plan || "").toLowerCase()] ?? -1;
          return br - ar;
        });

      setResults(matched);
    } catch (e) {
      console.error(e);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const reset = () => {
    setStep(1);
    setRange(null);
    setAvailableGalleries(null);
    setSelectedUrls(new Set());
    onSelfieChange(null);
    setBib("");
    setResults(null);
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress */}
      <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-8">
        {[1, 2, 3, 4].map((n, i) => (
          <div key={n} className="flex items-center gap-1.5 sm:gap-2">
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors",
                step >= (n as 1 | 2 | 3 | 4)
                  ? "bg-hawk-orange border-hawk-orange text-white"
                  : "bg-card border-border text-muted-foreground",
              )}
            >
              {step > n ? <Check size={16} /> : n}
            </div>
            {i < 3 && <div className={cn("h-0.5 w-6 sm:w-12", step > n ? "bg-hawk-orange" : "bg-border")} />}
          </div>
        ))}
      </div>


      {/* Step 1: date */}
      {step === 1 && (
        <div>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-display font-bold mb-2">When was your event?</h2>
            <p className="text-muted-foreground">Pick a date range so we can narrow the search.</p>
          </div>
          <div className="flex justify-end mb-3">
            <CountryPicker country={country} setCountry={setCountry} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {presets.map((p) => {
              const selected = range?.label === p.label;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setRange(p)}
                  className={cn(
                    "p-5 rounded-2xl border-2 text-left transition-all hover:shadow-lg hover:-translate-y-0.5",
                    selected
                      ? "border-hawk-orange bg-hawk-orange/10"
                      : "border-border bg-card",
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarIcon size={18} className="text-hawk-orange" />
                    <span className="font-display font-semibold">{p.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(p.from.toISOString())}
                    {p.from.toDateString() !== p.to.toDateString() && ` – ${formatDate(p.to.toISOString())}`}
                  </p>
                </button>
              );
            })}
            <Popover open={customOpen} onOpenChange={setCustomOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "p-5 rounded-2xl border-2 text-left transition-all hover:shadow-lg hover:-translate-y-0.5",
                    range?.label === "Custom"
                      ? "border-hawk-orange bg-hawk-orange/10"
                      : "border-dashed border-border bg-card",
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarIcon size={18} className="text-hawk-orange" />
                    <span className="font-display font-semibold">Pick a date</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {range?.label === "Custom"
                      ? formatDate(range.from.toISOString())
                      : "Any other day"}
                  </p>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={range?.label === "Custom" ? range.from : undefined}
                  onSelect={(d: Date | undefined) => {
                    if (d) {
                      setRange({
                        from: startOfDay(d),
                        to: endOfDay(d),
                        label: "Custom",
                      });
                      setCustomOpen(false);
                    }
                  }}
                  disabled={(d) => d > new Date()}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex justify-end mt-8">
            <Button size="lg" disabled={!canProceed2} onClick={() => setStep(2)} className="bg-hawk-orange hover:bg-hawk-orange/90">
              Continue <ArrowRight className="ml-1" size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: pick galleries */}
      {step === 2 && (
        <div>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-display font-bold mb-2">Which event were you at?</h2>
            <p className="text-muted-foreground">
              Select one or more galleries to search. Variants of the same event are grouped together.
            </p>
          </div>

          {loadingAvailable ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-hawk-orange" size={28} />
            </div>
          ) : !availableGalleries || availableGalleries.length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-muted-foreground mb-4">
                No galleries available in {COUNTRIES.find((c) => c.code === country)?.name ?? country} for the selected date.
              </p>
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft size={16} /> Pick another date
              </Button>
            </Card>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4 text-sm">
                <span className="text-muted-foreground">
                  <span className="font-display font-semibold text-foreground">{selectedUrls.size}</span> of {availableGalleries.length} selected
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedUrls(new Set(availableGalleries.map((h) => h.url)))}
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedUrls(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {groupedAvailable.map((group) => {
                  const selectedCount = group.hits.filter((h) => selectedUrls.has(h.url)).length;
                  const allSelected = selectedCount === group.hits.length;
                  const someSelected = selectedCount > 0 && !allSelected;
                  const isGroup = group.hits.length > 1;
                  const groupCover = group.hits.find((h) => h.media?.featuredImage)?.media?.featuredImage;
                  const groupCoverPos = group.hits.find((h) => h.media?.featuredImage)?.mediaBackgroundPosition;
                  return (
                    <Card key={group.key} className="overflow-hidden">
                      <div className="p-3 sm:p-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <Checkbox
                            checked={allSelected ? true : someSelected ? "indeterminate" : false}
                            onCheckedChange={() => toggleGroup(group.hits)}
                            className="mt-0.5"
                          />
                          <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-muted">
                            <img
                              src={groupCover || galleryPlaceholder}
                              alt={group.label}
                              loading="lazy"
                              className="w-full h-full object-cover"
                              style={groupCoverPos ? { objectPosition: groupCoverPos } : undefined}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-display font-semibold text-foreground line-clamp-2">{group.label}</p>
                            {!isGroup && group.hits[0].companyName && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">{group.hits[0].companyName}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {isGroup
                                ? `${group.hits.length} galleries · ${selectedCount} selected`
                                : formatDate(group.hits[0].date)}
                            </p>
                          </div>
                        </label>

                        {isGroup && (
                          <div className="mt-3 ml-7 pl-3 border-l-2 border-border space-y-2">
                            {group.hits.map((h) => (
                              <label key={h.url} className="flex items-start gap-3 cursor-pointer text-sm">
                                <Checkbox
                                  checked={selectedUrls.has(h.url)}
                                  onCheckedChange={() => toggleUrl(h.url)}
                                  className="mt-0.5"
                                />
                                <div className="flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-muted">
                                  <img
                                    src={h.media?.featuredImage || galleryPlaceholder}
                                    alt={h.name}
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                    style={h.mediaBackgroundPosition ? { objectPosition: h.mediaBackgroundPosition } : undefined}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-foreground line-clamp-2">{h.name}</p>
                                  {h.companyName && (
                                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{h.companyName}</p>
                                  )}
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {formatDate(h.date)}
                                    {h.noItems != null && ` · ${h.noItems.toLocaleString()} photos`}
                                  </p>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          <div className="flex gap-3 mt-6">
            <Button variant="outline" size="lg" onClick={() => setStep(1)} className="flex-shrink-0">
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <Button
              size="lg"
              disabled={!canProceed3}
              onClick={() => setStep(3)}
              className="flex-1 bg-hawk-orange hover:bg-hawk-orange/90"
            >
              Continue <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: selfie + bib */}
      {step === 3 && (
        <div>
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-2">Help us find you</h2>
            <p className="text-muted-foreground text-sm sm:text-base px-4">
              A selfie works best. A bib number helps too. Add either or both.
            </p>
          </div>

          {/* Hidden inputs */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="user"
            className="hidden"
            onChange={(e) => onSelfieChange(e.target.files?.[0] ?? null)}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onSelfieChange(e.target.files?.[0] ?? null)}
          />

          {/* Selfie card */}
          <Card className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-full bg-hawk-orange/10 text-hawk-orange flex items-center justify-center">
                <ScanFace size={18} />
              </div>
              <div>
                <h3 className="font-display font-semibold leading-tight">Your selfie</h3>
                <p className="text-xs text-muted-foreground">We'll match your face in the photos</p>
              </div>
            </div>

            {selfiePreview ? (
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="relative">
                  <img
                    src={selfiePreview}
                    alt="Selfie preview"
                    className="w-32 h-32 sm:w-28 sm:h-28 object-cover rounded-2xl ring-2 ring-hawk-orange/30"
                  />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-hawk-orange text-white flex items-center justify-center shadow-lg">
                    <Check size={14} />
                  </div>
                </div>
                <div className="flex-1 w-full flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-11"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <Camera size={16} /> Retake
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="flex-1 h-11"
                    onClick={() => onSelfieChange(null)}
                  >
                    <X size={16} /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-2 p-5 sm:p-6 rounded-2xl border-2 border-dashed border-border hover:border-hawk-orange hover:bg-hawk-orange/5 active:bg-hawk-orange/10 transition-colors text-foreground min-h-[120px]"
                >
                  <Camera size={28} className="text-hawk-orange" />
                  <span className="text-sm font-semibold">Take selfie</span>
                  <span className="text-[11px] text-muted-foreground">Use your camera</span>
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-2 p-5 sm:p-6 rounded-2xl border-2 border-dashed border-border hover:border-hawk-orange hover:bg-hawk-orange/5 active:bg-hawk-orange/10 transition-colors text-foreground min-h-[120px]"
                >
                  <Upload size={28} className="text-hawk-orange" />
                  <span className="text-sm font-semibold">Upload photo</span>
                  <span className="text-[11px] text-muted-foreground">JPG or PNG</span>
                </button>
              </div>
            )}
          </Card>

          {/* OR divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">and / or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Bib card */}
          <Card className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-full bg-hawk-orange/10 text-hawk-orange flex items-center justify-center">
                <Hash size={18} />
              </div>
              <div>
                <h3 className="font-display font-semibold leading-tight">Bib number</h3>
                <p className="text-xs text-muted-foreground">If you wore one during the event</p>
              </div>
            </div>
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              placeholder="e.g. 1423"
              value={bib}
              onChange={(e) => setBib(e.target.value.replace(/\s+/g, "").slice(0, 12))}
              className="h-14 text-xl text-center font-display font-semibold tracking-widest"
            />
          </Card>

          {/* Status */}
          <div className="mt-5 p-4 rounded-xl bg-muted/50 border border-border text-center">
            <p className="text-sm">
              <span className="font-display font-semibold text-foreground">{selectedUrls.size}</span>{" "}
              <span className="text-muted-foreground">
                {selectedUrls.size === 1 ? "gallery" : "galleries"} will be searched
              </span>
            </p>
          </div>

          {/* Sticky-ish actions on mobile */}
          <div className="flex gap-3 mt-6">
            <Button variant="outline" size="lg" onClick={() => setStep(2)} className="flex-shrink-0">
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <Button
              size="lg"
              disabled={!canSearch}
              onClick={runSearch}
              className="flex-1 bg-hawk-orange hover:bg-hawk-orange/90 h-14 text-base"
            >
              <Sparkles size={18} /> Find my photos
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: results */}
      {step === 4 && (
        <div>
          {searching ? (
            <div className="text-center py-20">
              <Loader2 className="animate-spin text-hawk-orange mx-auto mb-4" size={36} />
              <p className="font-display font-semibold text-lg">Scanning galleries…</p>
              <p className="text-sm text-muted-foreground mt-2">
                Looking through {range && `${formatDate(range.from.toISOString())} – ${formatDate(range.to.toISOString())}`} in{" "}
                {COUNTRIES.find((c) => c.code === country)?.name ?? country}
              </p>
              {searchProgress.total > 0 && (
                <p className="text-xs text-muted-foreground mt-3">
                  {searchProgress.done} / {searchProgress.total} galleries searched
                </p>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-display font-bold">Galleries with possible matches</h2>
                  <p className="text-sm text-muted-foreground">
                    {results?.length ?? 0} galleries found · open one to see your photos
                  </p>
                </div>
                <Button variant="outline" onClick={reset}>Start over</Button>
              </div>
              {results && results.length > 0 && (() => {
                const byDay = new Map<string, number>();
                for (const r of results) {
                  const ms = Number(r.date);
                  const key = Number.isFinite(ms)
                    ? new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : String(r.date);
                  byDay.set(key, (byDay.get(key) ?? 0) + (r.matchCount ?? 0));
                }
                const entries = Array.from(byDay.entries());
                return (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {entries.map(([day, count]) => (
                      <Badge key={day} variant="secondary" className="px-3 py-1.5 text-sm">
                        {day} · {count} {count === 1 ? "match" : "matches"}
                      </Badge>
                    ))}
                  </div>
                );
              })()}
              {results && results.length > 0 ? (
                <div className="grid lg:grid-cols-2 gap-5">
                  {results.map((hit, i) => {
                    const thumbs = hit.thumbs ?? [];
                    const previewThumbs = thumbs.slice(0, 8);
                    const remaining = (hit.matchCount ?? thumbs.length) - previewThumbs.length;
                    return (
                      <Card key={`${hit.url}-${i}`} className="overflow-hidden flex flex-col hover:shadow-xl transition-shadow">
                        {/* Header */}
                        <div className="p-4 sm:p-5 pb-3 flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-display font-semibold line-clamp-2 leading-tight">
                              {hit.name}
                            </h3>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CalendarIcon size={12} />
                                {formatDate(hit.date)}
                              </span>
                              {hit.companyName && <span className="truncate">{hit.companyName}</span>}
                            </div>
                          </div>
                          {hit.matchCount != null && hit.matchCount > 0 && (
                            <Badge className="bg-hawk-orange text-white border-0 flex-shrink-0">
                              {hit.matchCount} {hit.matchCount === 1 ? "match" : "matches"}
                            </Badge>
                          )}
                        </div>

                        {/* Thumbnails */}
                        {previewThumbs.length > 0 ? (
                          <div className="px-4 sm:px-5">
                            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin">
                              {previewThumbs.map((src, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() =>
                                    setLightbox({
                                      images: hit.fulls && hit.fulls.length ? hit.fulls : (hit.thumbs ?? []),
                                      index: idx,
                                      galleryUrl: appendGuids(hit.url, hit.guids),
                                      galleryName: hit.name,
                                    })
                                  }
                                  className="relative flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-muted snap-start group/thumb cursor-zoom-in"
                                  aria-label={`Open match ${idx + 1} in lightbox`}
                                >
                                  <img
                                    src={src}
                                    alt={`${hit.name} match ${idx + 1}`}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover/thumb:scale-110 transition-transform duration-300"
                                  />
                                  {idx === previewThumbs.length - 1 && remaining > 0 && (
                                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-display font-bold text-lg">
                                      +{remaining}
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="px-4 sm:px-5">
                            <div className="aspect-[16/6] rounded-xl bg-muted overflow-hidden">
                              <img
                                src={hit.media?.featuredImage || galleryPlaceholder}
                                alt={hit.name}
                                loading="lazy"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        )}

                        {/* CTA */}
                        <div className="p-4 sm:p-5 pt-3 mt-auto">
                          <Button
                            asChild
                            className="w-full h-12 bg-hawk-orange hover:bg-hawk-orange/90 text-white font-semibold"
                          >
                            <a href={appendGuids(hit.url, hit.guids)} target="_blank" rel="noopener noreferrer">
                              View in gallery
                              <ExternalLink size={16} />
                            </a>
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-muted-foreground">No galleries matched. Try a wider date range or different country.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent
          className="max-w-5xl w-[95vw] p-0 bg-hawk-navy border-hawk-navy-light overflow-hidden"
          aria-describedby={undefined}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{lightbox?.galleryName ?? "Photo preview"}</DialogTitle>
          </DialogHeader>
          {lightbox && (() => {
            const total = lightbox.images.length;
            const idx = lightbox.index;
            const go = (delta: number) =>
              setLightbox((prev) => prev ? { ...prev, index: (prev.index + delta + total) % total } : prev);
            return (
              <div className="relative">
                <div className="relative w-full aspect-[4/3] sm:aspect-video bg-black flex items-center justify-center">
                  <img
                    src={lightbox.images[idx]}
                    alt={`${lightbox.galleryName} match ${idx + 1}`}
                    className="max-w-full max-h-[75vh] w-auto h-auto object-contain"
                  />
                  {total > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => go(-1)}
                        aria-label="Previous"
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur"
                      >
                        <ArrowLeft size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => go(1)}
                        aria-label="Next"
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur"
                      >
                        <ArrowRight size={18} />
                      </button>
                      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-medium backdrop-blur">
                        {idx + 1} / {total}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-hawk-navy text-white">
                  <div className="min-w-0">
                    <p className="font-display font-semibold truncate">{lightbox.galleryName}</p>
                    <p className="text-xs text-white/60">Preview · open the gallery to download</p>
                  </div>
                  <Button
                    asChild
                    className="bg-hawk-orange hover:bg-hawk-orange/90 text-white flex-shrink-0"
                  >
                    <a href={lightbox.galleryUrl} target="_blank" rel="noopener noreferrer">
                      View in gallery <ExternalLink size={16} />
                    </a>
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------- Page ---------- */
const FindPhotosPage = ({ search }: { search: string }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const params = readDirectoryParams(search);
  // A URL that names a country or a page (?country=my, ?before=…) is a directory listing: show it, never the chooser.
  const explicitListing = !!(params.country || params.before);
  // The server picked the country from ?country= or Accept-Language (INITIAL_COUNTRY_KEY); start from the same one
  // so the first render matches the HTML. After a client-side navigation, detect it from the browser.
  const [country, setCountryState] = useState<string>(
    () => params.country ?? queryClient.getQueryData<string>(INITIAL_COUNTRY_KEY) ?? detectCountry(),
  );
  const [before, setBefore] = useState<string | null>(params.before);
  const setCountry = useCallback((c: string) => {
    setCountryState(c);
    setBefore(null);
  }, []);
  const [mode, setMode] = useState<"choose" | "browse" | "wizard">(() =>
    !explicitListing && supportsWizard(country) ? "choose" : "browse",
  );
  const wizardAvailable = supportsWizard(country);

  // React to country changes: if it no longer supports the wizard, force browse.
  // If it newly supports the wizard, take the user to the method chooser.
  const prevCountryRef = useRef(country);
  useEffect(() => {
    const prev = prevCountryRef.current;
    if (prev !== country) {
      const wasWizard = supportsWizard(prev);
      const isWizard = supportsWizard(country);
      if (wasWizard && !isWizard) setMode("browse");
      else if (!wasWizard && isWizard) setMode("choose");
      prevCountryRef.current = country;
    }
  }, [country]);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-hawk-navy to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
              {t("findPhotos.title")}
            </h1>
            <p className="text-white/70 mb-8">{t("findPhotos.subtitle")}</p>
            {/* Where the guides are on: a quiet "how it works" link for people who can't find themselves. */}
            <IfGuides>
              <p className="-mt-4 mb-8 text-sm">
                <Link to={guidePath("find-your-race-photos")} className="font-semibold text-white/80 underline-offset-4 hover:text-white hover:underline">
                  Can't find yourself? Read our guide to finding your race photos
                </Link>
              </p>
            </IfGuides>

            {wizardAvailable && mode !== "choose" && (
              <div className="flex justify-center mb-6">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMode("choose")}
                  className="text-white/80 hover:text-white hover:bg-white/10"
                >
                  <ArrowLeft className="mr-1" size={16} /> {t("findPhotos.changeMethod")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          {wizardAvailable && mode === "choose" && (
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
              <button
                type="button"
                onClick={() => setMode("browse")}
                className="group text-left p-8 rounded-3xl border-2 border-border bg-card hover:border-hawk-orange hover:shadow-2xl hover:-translate-y-1 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-hawk-orange/10 text-hawk-orange flex items-center justify-center mb-5 group-hover:bg-hawk-orange group-hover:text-white transition-colors">
                  <LayoutGrid size={28} />
                </div>
                <h2 className="text-2xl font-display font-bold mb-2">{t("findPhotos.browseTitle")}</h2>
                <p className="text-muted-foreground">{t("findPhotos.browseBody")}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-hawk-orange font-semibold">
                  {t("findPhotos.browseCta")} <ArrowRight size={16} />
                </span>
              </button>

              <button
                type="button"
                onClick={() => setMode("wizard")}
                className="group text-left p-8 rounded-3xl border-2 border-border bg-card hover:border-hawk-orange hover:shadow-2xl hover:-translate-y-1 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-hawk-orange/10 text-hawk-orange flex items-center justify-center mb-5 group-hover:bg-hawk-orange group-hover:text-white transition-colors">
                  <ScanFace size={28} />
                </div>
                <h2 className="text-2xl font-display font-bold mb-2">{t("findPhotos.wizardTitle")}</h2>
                <p className="text-muted-foreground">{t("findPhotos.wizardBody")}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-hawk-orange font-semibold">
                  {t("findPhotos.wizardCta")} <ArrowRight size={16} />
                </span>
              </button>
            </div>
          )}

          {mode === "browse" && <BrowseView country={country} setCountry={setCountry} before={before} />}
          {mode === "wizard" && <WizardView country={country} setCountry={setCountry} />}
        </div>
      </section>

      <CountryDirectory />

      <Footer />
    </div>
  );
};

/** The directory page. A new ?country= / ?before= (a pagination or country link) starts it afresh. */
const FindPhotos = () => {
  const { search } = useLocation();
  return <FindPhotosPage key={search} search={search} />;
};

export default FindPhotos;
